import React, { FunctionComponent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import gql from 'graphql-tag';

import PATHS from '../../constants/paths';
import { FundraisersListQuery } from '../../../types/__generated__';
import { SIZE_136_136 } from '../../constants/imageTemplateStrings';

import BenefactorListItem from '../BenefactorListItem/BenefactorListItem';
import PageSection from '../PageSection/PageSection';
import LoadMoreButton from '../LoadMoreButton/LoadMoreButton';
import BenefactorListItemSkeleton from '../BenefactorListItem/BenefactorListItemSkeleton';
import useFullPageQuery from '../../hooks/useFullPageQuery';

import styles from './FundraisersList.scss';

const DOCUMENT = gql`
  query FundraisersList($after: String, $type: PageType!, $slug: Slug!) {
    page(type: $type, slug: $slug) {
      id
      relationships {
        config {
          membersDisabled
        }
        members(first: 5, after: $after) {
          edges {
            node {
              id
              slug
              owner {
                avatar
                name
              }
              donations(first: 1) {
                totalCount
              }
              donationSummary {
                totalAmount {
                  value
                  currencyCode
                }
              }
              targetWithCurrency {
                value
              }
            }
          }
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
`;

const FundraisersList: FunctionComponent = () => {
  const { t } = useTranslation('common', { useSuspense: false });
  const { loading, error, data, fetchMore } = useFullPageQuery<
    FundraisersListQuery
  >(DOCUMENT, {
    notifyOnNetworkStatusChange: true,
  });

  if (loading || error) {
    return (
      <PageSection title={t('fundraisers.title', 'Fundraisers')} hasBorder>
        <BenefactorListItemSkeleton animated={false} />
      </PageSection>
    );
  }

  if (
    !data?.page?.relationships?.members ||
    data?.page?.relationships?.config?.membersDisabled
  ) {
    return null;
  }

  const {
    members: { edges, totalCount },
  } = data.page.relationships;

  async function handleFetchMore() {
    await fetchMore({
      variables: {
        after: data?.page?.relationships?.members.pageInfo.endCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (
          !previousResult?.page?.relationships?.members ||
          !fetchMoreResult?.page?.relationships?.members
        ) {
          return previousResult;
        }

        return {
          page: {
            ...previousResult.page,
            relationships: {
              ...fetchMoreResult.page.relationships,
              members: {
                ...fetchMoreResult.page.relationships.members,
                edges: [
                  ...previousResult.page.relationships.members.edges,
                  ...fetchMoreResult.page.relationships.members.edges,
                ],
              },
            },
          },
        };
      },
    });
  }

  return (
    <PageSection
      title={
        totalCount === 0
          ? t('fundraisers.noFundraisers', 'No fundraisers yet')
          : t('fundraisers.title', 'Fundraisers')
      }
      hasBorder
      count={totalCount}
      className={styles.container}
    >
      <ul data-qa="fundraisers-list">
        {edges.map(fundraiser => {
          const {
            id,
            owner,
            donations,
            donationSummary,
            slug,
            targetWithCurrency,
          } = fundraiser.node;

          let totaliserValue;
          if (targetWithCurrency) {
            totaliserValue = Math.round(
              (donationSummary?.totalAmount?.value / targetWithCurrency.value) *
                100,
            );
          }

          return (
            <BenefactorListItem
              key={id}
              title={owner?.name}
              image={
                owner?.avatar ? `${owner?.avatar}?template=${SIZE_136_136}` : ''
              }
              currencyCode={donationSummary?.totalAmount?.currencyCode || 'GBP'}
              amount={
                donationSummary?.totalAmount?.value
                  ? donationSummary.totalAmount.value / 100
                  : 0
              }
              totaliserValue={totaliserValue}
              supporterCount={donations?.totalCount || 0}
              url={`${PATHS.FRP}${slug}`}
            />
          );
        })}
      </ul>
      {data.page.relationships.members.pageInfo.hasNextPage && (
        <LoadMoreButton
          className="jg-btn"
          loading={loading}
          onClick={handleFetchMore}
          trackingData={{
            action_name: 'click',
            subtype: 'button',
            event: 'click',
            event_value: 'show more',
            sub_section: 'campaign members',
          }}
        >
          <Trans i18nKey="fundraisers.showMore">
            Show more
            <span className="jg-sr-only"> fundraisers</span>
          </Trans>
        </LoadMoreButton>
      )}
    </PageSection>
  );
};

export default FundraisersList;
